import {
    ClientEndpoint,
    ClientEndpointType,
    EventTrigger,
    EventTriggerClientEndpoint,
    EventTriggerType,
    LuxcomClientEndpoint,
    LuxcomEndpointConfig,
    LuxcomEndpointConfigDetector,
    DetectionZoneChangeEventTrigger,
    DetectionZoneDwellEventTrigger,
    DetectionZoneEntryEventTrigger,
    DetectionZoneExitEventTrigger,
    DetectionZonePresenceEventTrigger,
    ImpendingZoneEntryEventTrigger,
    NearMissEventTrigger,
    ScreenLineChangeEventTrigger,
    ScreenLineInboundEventTrigger,
    ScreenLineOutboundEventTrigger,
    SpeedingEventTrigger,
    TargetDetectedEventTrigger,
    WrongWayEventTrigger,
} from "@app/shared";

export function isLuxcomEndpoint(endpoint: ClientEndpoint | null | undefined): endpoint is LuxcomClientEndpoint {
    return endpoint?.endpointType === ClientEndpointType.Luxcom;
}

export const BIU_NUMBERS = [1, 2, 3, 4] as const;
export const DETECTOR_NUMBERS = Array.from({ length: 16 }, (_, i) => i + 1);

export type BiuNumber = (typeof BIU_NUMBERS)[number];

export const TRIGGER_TYPE_OPTIONS: {
    $type: string;
    triggerType: EventTriggerType;
    label: string;
    hasDetectionZone: boolean;
    hasScreenLine: boolean;
}[] = [
    { $type: DetectionZoneChangeEventTrigger.$type, triggerType: EventTriggerType.DetectionZoneChange, label: "Detection Zone Change", hasDetectionZone: true, hasScreenLine: false },
    { $type: DetectionZoneDwellEventTrigger.$type, triggerType: EventTriggerType.DetectionZoneDwell, label: "Detection Zone Dwell", hasDetectionZone: true, hasScreenLine: false },
    { $type: DetectionZoneEntryEventTrigger.$type, triggerType: EventTriggerType.DetectionZoneEntry, label: "Detection Zone Entry", hasDetectionZone: true, hasScreenLine: false },
    { $type: DetectionZoneExitEventTrigger.$type, triggerType: EventTriggerType.DetectionZoneExit, label: "Detection Zone Exit", hasDetectionZone: true, hasScreenLine: false },
    { $type: DetectionZonePresenceEventTrigger.$type, triggerType: EventTriggerType.DetectionZonePresence, label: "Detection Zone Presence", hasDetectionZone: true, hasScreenLine: false },
    { $type: ImpendingZoneEntryEventTrigger.$type, triggerType: EventTriggerType.ImpendingZoneEntry, label: "Impending Zone Entry", hasDetectionZone: true, hasScreenLine: false },
    { $type: NearMissEventTrigger.$type, triggerType: EventTriggerType.NearMissEvent, label: "Near Miss", hasDetectionZone: false, hasScreenLine: false },
    { $type: ScreenLineChangeEventTrigger.$type, triggerType: EventTriggerType.ScreenLineChange, label: "Screen Line Change", hasDetectionZone: false, hasScreenLine: true },
    { $type: ScreenLineInboundEventTrigger.$type, triggerType: EventTriggerType.ScreenLineInbound, label: "Screen Line Inbound", hasDetectionZone: false, hasScreenLine: true },
    { $type: ScreenLineOutboundEventTrigger.$type, triggerType: EventTriggerType.ScreenLineOutbound, label: "Screen Line Outbound", hasDetectionZone: false, hasScreenLine: true },
    { $type: SpeedingEventTrigger.$type, triggerType: EventTriggerType.SpeedingEvent, label: "Speeding", hasDetectionZone: true, hasScreenLine: true },
    { $type: TargetDetectedEventTrigger.$type, triggerType: EventTriggerType.TargetDetected, label: "Target Detected", hasDetectionZone: true, hasScreenLine: false },
    { $type: WrongWayEventTrigger.$type, triggerType: EventTriggerType.WrongWayEvent, label: "Wrong Way", hasDetectionZone: true, hasScreenLine: false },
];

export const getTriggerTypeLabel = ($type: string | undefined): string => {
    if (!$type) return "-";
    const found = TRIGGER_TYPE_OPTIONS.find((t) => t.$type === $type);
    return found?.label ?? $type;
};

export const getTriggerTypeConfig = ($type: string | undefined) => {
    if (!$type) return undefined;
    return TRIGGER_TYPE_OPTIONS.find((t) => t.$type === $type);
};

export interface BiuDetectorRowData {
    biuNum: BiuNumber;
    detectorNum: number;
    expression?: string;
    isConfigured: boolean;

    eventTriggerId?: string;
    eventTriggerClientEndpointId?: string;
    luxcomEndpointId?: string;

    triggerType?: string;
    triggerName?: string;
    detectionZoneId?: string;
    detectionZoneName?: string;
    screenLineId?: string;
    screenLineName?: string;
    facilityIds?: string[];
    facilityNames?: string[];
    deviceIds?: string[];
    deviceNames?: string[];
    targetFilter?: number;
    processOnDevice?: boolean;

    minValue?: number;
    maxValue?: number;
    additionalValue?: number;

    sourceEventTrigger?: EventTrigger;
    sourceEventTriggerClientEndpoint?: EventTriggerClientEndpoint;
}

export interface BiuDataMap {
    [biuNum: number]: {
        [detectorNum: number]: BiuDetectorRowData[];
    };
}

export const parseLuxcomEndpointConfig = (configJson: string | undefined): LuxcomEndpointConfig | undefined => {
    if (!configJson || configJson.length < 2) return undefined;
    try {
        return new LuxcomEndpointConfig(JSON.parse(configJson));
    } catch {
        return undefined;
    }
};

export const transformToBiuData = (eventTriggers: EventTrigger[] | undefined, luxcomEndpoints: LuxcomClientEndpoint[]): BiuDataMap => {
    const result: BiuDataMap = {};

    for (const biuNum of BIU_NUMBERS) {
        result[biuNum] = {};
        for (const detectorNum of DETECTOR_NUMBERS) {
            result[biuNum][detectorNum] = [];
        }
    }

    if (!eventTriggers || luxcomEndpoints.length === 0) {
        return result;
    }

    const luxcomEndpointIds = new Set(luxcomEndpoints.map((le) => le.id));

    for (const trigger of eventTriggers) {
        if (!trigger.eventTriggerClientEndpoints) continue;

        for (const etc of trigger.eventTriggerClientEndpoints) {
            const isLuxcomByType = isLuxcomEndpoint(etc.clientEndpoint);
            const isLuxcomById = etc.clientEndpointId && luxcomEndpointIds.has(etc.clientEndpointId);

            if (!isLuxcomByType && !isLuxcomById) {
                continue;
            }

            const config = parseLuxcomEndpointConfig(etc.endpointConfig);
            if (!config || !config.detectors) {
                continue;
            }

            const biuNum = config.biuNum as BiuNumber;
            if (!BIU_NUMBERS.includes(biuNum)) {
                continue;
            }

            for (const detector of config.detectors) {
                if (detector.detector < 1 || detector.detector > 16) continue;

                const rowData: BiuDetectorRowData = {
                    biuNum,
                    detectorNum: detector.detector,
                    expression: detector.expression,
                    isConfigured: true,

                    eventTriggerId: trigger.id,
                    eventTriggerClientEndpointId: etc.eventTriggerId,
                    luxcomEndpointId: etc.clientEndpointId,

                    triggerType: trigger.$type,
                    triggerName: trigger.name,
                    detectionZoneId: trigger.detectionZoneId,
                    detectionZoneName: trigger.detectionZone?.name,
                    screenLineId: trigger.screenLineId,
                    screenLineName: trigger.screenLine?.name,
                    facilityIds: trigger.eventTriggerFacilities?.map((f) => f.facilityId ?? "").filter(Boolean),
                    facilityNames: trigger.facilities?.map((f) => f.name ?? "").filter(Boolean),
                    deviceIds: trigger.eventTriggerDevices?.map((d) => d.deviceId ?? "").filter(Boolean),
                    deviceNames: trigger.devices?.map((d) => d.name ?? "").filter(Boolean),
                    targetFilter: trigger.targetFilter as number,
                    processOnDevice: trigger.processOnDevice,
                    minValue: trigger.minValue,
                    maxValue: trigger.maxValue,
                    additionalValue: trigger.additionalValue,

                    sourceEventTrigger: trigger,
                    sourceEventTriggerClientEndpoint: etc,
                };

                result[biuNum][detector.detector].push(rowData);
            }
        }
    }

    return result;
};

export const createEmptyDetectorRow = (biuNum: BiuNumber, detectorNum: number, luxcomEndpointId?: string): BiuDetectorRowData => ({
    biuNum,
    detectorNum,
    isConfigured: false,
    luxcomEndpointId,
});

export const serializeLuxcomEndpointConfig = (biuNum: number, detectors: LuxcomEndpointConfigDetector[]): string => {
    const config = new LuxcomEndpointConfig({
        biuNum,
        detectors: detectors.map((d) => ({ detector: d.detector, expression: d.expression })),
    });
    return JSON.stringify(config);
};
